import type { ReactNode } from 'react'
import { Check, CheckCircle2, XCircle, CircleDashed, Clock, Circle, type LucideIcon } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import type { ContentModel, ModelProperty } from './api/content'

// Types/fields we never show as a list column (heavy, noisy, or non-scalar).
const SKIP_TYPES = new Set(['qrCode', 'image', 'media', 'html', 'wysiwyg', 'file', 'password', 'json', null as unknown as string])
const SKIP_NAMES = new Set([
  'qrCode', 'payment', 'billingAddress', 'privacy', 'commercialPrivacy', 'researchPrivacy',
  'businessSDI', 'businessVatId', 'businessName', 'businessEmail', 'businessPec', 'isPublicAdministration',
  'isPublicAdminitration', 'cancellationCode', 'services', 'description',
])
const MONEY_RE = /price|total|amount|importo|prezzo|costo|tariffa/i

export interface Column {
  field: string
  label: string
  type: string | null
}

/** Pick a sensible set of list columns from a model's field definitions. */
export function pickColumns(model: ContentModel | undefined, max = 7): Column[] {
  if (!model) return []
  const cols: Column[] = []
  // Always surface status first if present.
  const status = model.properties.find((p) => p.name === 'status')
  if (status) cols.push({ field: 'status', label: status.label, type: 'status' })
  for (const p of model.properties) {
    if (cols.length >= max) break
    if (p.name === 'status') continue
    if (SKIP_TYPES.has(p.type as string)) continue
    if (SKIP_NAMES.has(p.name)) continue
    cols.push({ field: p.name, label: p.label || p.name, type: p.type })
  }
  return cols
}

function refLabel(o: unknown): string {
  if (o == null) return ''
  if (typeof o !== 'object') return String(o)
  const r = o as { elements?: Record<string, unknown>; id?: string }
  const el = r.elements ?? {}
  return String(el.label ?? el.name ?? el.code ?? el.title ?? r.id ?? '')
}

function money(cents: unknown): string {
  const n = Number(cents)
  if (!Number.isFinite(n)) return String(cents)
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n / 100)
}

function fmtDate(v: unknown): string {
  if (!v || typeof v !== 'string') return '—'
  try {
    const d = parseISO(v)
    const hasTime = !/T00:00:00/.test(v)
    return format(d, hasTime ? 'dd/MM/yy HH:mm' : 'dd/MM/yyyy')
  } catch {
    return String(v)
  }
}

const STATUS_STYLE: Record<string, string> = {
  CONFIRMED: 'bg-green-100 text-green-700',
  PAID: 'bg-green-100 text-green-700',
  ACTIVE: 'bg-green-100 text-green-700',
  DRAFT: 'bg-amber-100 text-amber-700',
  PENDING: 'bg-amber-100 text-amber-700',
  CANCELLED: 'bg-brand-100 text-brand-700',
  CANCELED: 'bg-brand-100 text-brand-700',
  EXPIRED: 'bg-ink-200 text-ink-600',
}

export function StatusBadge({ value }: { value: unknown }) {
  const v = String(value ?? '').toUpperCase()
  if (!v) return <span className="text-ink-300">—</span>
  return <span className={`badge ${STATUS_STYLE[v] ?? 'bg-ink-100 text-ink-600'}`}>{v}</span>
}

// Icon-only status (with a hover tooltip for the label).
const STATUS_ICON: Record<string, { Icon: LucideIcon; cls: string; label: string }> = {
  CONFIRMED: { Icon: CheckCircle2, cls: 'text-green-600', label: 'Confermata' },
  PAID: { Icon: CheckCircle2, cls: 'text-green-600', label: 'Pagata' },
  ACTIVE: { Icon: CheckCircle2, cls: 'text-green-600', label: 'Attiva' },
  DRAFT: { Icon: CircleDashed, cls: 'text-amber-500', label: 'Draft' },
  PENDING: { Icon: Clock, cls: 'text-amber-500', label: 'In attesa' },
  CANCELLED: { Icon: XCircle, cls: 'text-brand-600', label: 'Annullata' },
  CANCELED: { Icon: XCircle, cls: 'text-brand-600', label: 'Annullata' },
  EXPIRED: { Icon: Circle, cls: 'text-ink-400', label: 'Scaduta' },
}

export function StatusIcon({ value }: { value: unknown }) {
  const v = String(value ?? '').toUpperCase()
  if (!v) return <span className="text-ink-300">—</span>
  const s = STATUS_ICON[v] ?? { Icon: Circle, cls: 'text-ink-400', label: v }
  return (
    <span title={s.label} className="inline-flex">
      <s.Icon className={`h-[18px] w-[18px] ${s.cls}`} aria-label={s.label} />
    </span>
  )
}

/** Render one cell value by column type. */
export function formatCell(value: unknown, col: Column): ReactNode {
  if (col.type === 'status') return <StatusIcon value={value} />
  if (value == null || value === '' || value === 'N/A') return <span className="text-ink-300">—</span>

  switch (col.type) {
    case 'reference':
      if (Array.isArray(value)) {
        const labels = value.map(refLabel).filter(Boolean)
        return labels.length ? labels.join(', ') : <span className="text-ink-300">—</span>
      }
      return refLabel(value) || <span className="text-ink-300">—</span>
    case 'date':
      return <span className="tnum">{fmtDate(value)}</span>
    case 'boolean':
      return value === true || value === 'true' ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : (
        <span className="text-ink-300">—</span>
      )
    case 'number':
      return <span className="tnum">{MONEY_RE.test(col.field) ? money(value) : String(value)}</span>
    default: {
      const s = String(value)
      return s.length > 48 ? <span title={s}>{s.slice(0, 48)}…</span> : s
    }
  }
}

export function modelTitle(model: ContentModel | undefined, fallback: string): string {
  return model?.pluralLabel || model?.label || fallback
}

export function isMoneyField(p: ModelProperty): boolean {
  return p.type === 'number' && MONEY_RE.test(p.name)
}
